import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useSelector } from "react-redux";
import { baseapi } from "../Environmentalurl";
import {
  ApplicationType,
  date,
  entity_id,
  locationGeo,
  Platform,
} from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function ResetRuPayPinDialog({ open, onClose }) {
  const rupayRefNo = useSelector((state) => state.auth.rupayRefNo);

  const mobile = sessionStorage.getItem("mobileNumber");
  const sessionId = sessionStorage.getItem("SessionID");
  const ip = useIPAddress();

  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");   // ✅ declared

  const isMatch = newPin.length === 4 && newPin === confirmPin;

  /* =========================
     RESET PIN FLOW
  ========================== */
  const handleResetPin = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      /* ---------- 1️⃣ GET CARD STATUS ---------- */
      const cardRes = await api.post(`${baseapi}/getcardstatus`, {
        ReqType: "Jan_Aadhaar_eWallet_GetCardStatus",
        Mobilenumber: mobile,
        SessionID: sessionId,
        ReferenceNumber: rupayRefNo,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });

      if (String(cardRes.data.Response_Code) !== "4001") {
        throw new Error(cardRes.data.Message || "Card status failed");
      }

      const { MaskedCard, EncKey } = cardRes.data;

      /* ---------- 2️⃣ HEX XOR GENERATOR ---------- */
      const hexRes = await api.post(
        `${baseapi}/hexXorgenerator`,
        { rpin: confirmPin, mobilenumber: mobile },
        { responseType: "text" },
      );
      const hexXor = hexRes.data;

      /* ---------- 3️⃣ ENCRYPT PIN ---------- */
      const encRes = await api.post(`${baseapi}/ewalletRupayEncryption3DES`, {
        ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
        Mobilenumber: mobile,
        Enc_Key: EncKey,
        Hex_XOR_Value: hexXor,
        Date: date,
        Platform,
        SessionID: sessionId,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });

      if (String(encRes.data.Response_Code) !== "1000") {
        throw new Error(encRes.data.Message || "Encryption failed");
      }

      const encryptedPin = encRes.data.RPIN;

      /* ---------- 4️⃣ FORGOT PIN API ---------- */
      const finalRes = await api.post(`${baseapi}/eWalletRuPayForgotPIN`, {
        ReqType: "Jan_Aadhaar_eWallet_RuPay_Forgot_PIN",
        Mobilenumber: mobile,
        ReferenceNumber: rupayRefNo,
        Optional_PIN: encryptedPin,
        SessionID: sessionId,
        CardNumber: MaskedCard,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });

      console.log("ForgotPIN final response:", finalRes.data);

      if (String(finalRes.data.Response_Code) === "1000") {
        setSuccess(true);
      } else {
        setErrorMsg(finalRes.data.Message || "PIN reset failed");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Something went wrong while resetting PIN");
    } finally {
      setLoading(false);
    }
  };

  // reset all state whenever dialog opens
  useEffect(() => {
    if (open) {
      setNewPin("");
      setConfirmPin("");
      setShow(false);
      setLoading(false);
      setSuccess(false);
      setErrorMsg("");
    }
  }, [open]);

  return (
    <>
      {/* MAIN DIALOG */}
      {open && !success && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: 380, bgcolor: "#fff", borderRadius: 4, p: 3 }}>
            <Box display="flex" justifyContent="space-between">
              <IconButton onClick={onClose}>
                <ArrowBackIcon />
              </IconButton>
              <Typography fontWeight={700}>Reset RuPay PIN</Typography>
              <IconButton>
                <HelpOutlineIcon sx={{ color: "#22c55e" }} />
              </IconButton>
            </Box>

            <Box mt={3} textAlign="center">
              <Typography fontWeight={700}>Set New PIN</Typography>
              <Typography fontSize={13} color="text.secondary">
                Identity verified. Please create a new 4-digit PIN.
              </Typography>
            </Box>

            <PinInput
              label="New RuPay PIN"
              value={newPin}
              setValue={setNewPin}
              show={show}
              setShow={setShow}
            />
            <PinInput
              label="Confirm New PIN"
              value={confirmPin}
              setValue={setConfirmPin}
              show={show}
              setShow={setShow}
            />

            {/* ERROR MESSAGE (inline, no browser alert) */}
            {errorMsg && (
              <Typography
                mt={2}
                fontSize={13}
                textAlign="center"
                sx={{ color: "#dc2626", fontWeight: 500 }}
              >
                {errorMsg}
              </Typography>
            )}

            <Button
              fullWidth
              disabled={!isMatch || loading}
              onClick={handleResetPin}
              sx={{
                mt: 3,
                height: 46,
                borderRadius: 2,
                backgroundColor: "#059669",
                color: "white",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#047857" },
              }}
            >
              {loading ? "Processing..." : "Reset PIN"}
            </Button>
          </Box>
        </Box>
      )}

      {/* SUCCESS POPUP */}
      {success && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 4000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 320,
              bgcolor: "#fff",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              boxShadow: "0px 20px 40px rgba(0,0,0,0.25)",
            }}
          >
            {/* GREEN TICK */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#DCFCE7",
                color: "#16A34A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 32,
                mx: "auto",
                mb: 2,
              }}
            >
              ✓
            </Box>

            <Typography fontSize={18} fontWeight={700} color="#065F46">
              PIN Changed Successfully
            </Typography>

            <Typography fontSize={13} color="text.secondary" mt={1}>
              Your RuPay PIN has been updated securely.
            </Typography>

            <Button
              fullWidth
              sx={{
                mt: 3,
                height: 44,
                borderRadius: 2,
                backgroundColor: "#059669",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "#047857" },
              }}
              onClick={() => {
                setSuccess(false);
                onClose();
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}

/* ---------- PIN INPUT ---------- */
function PinInput({ label, value, setValue, show, setShow }) {
  return (
    <Box mt={3}>
      <Typography fontSize={13}>{label}</Typography>
      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          border: "1px solid #22c55e",
          borderRadius: 2,
          px: 2,
          height: 48,
        }}
      >
        <input
          type={show ? "text" : "password"}
          value={value}
          maxLength={4}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
          style={{
            border: "none",
            outline: "none",
            flex: 1,
            letterSpacing: "10px",
            fontSize: 20,
          }}
        />
        <IconButton onClick={() => setShow(!show)}>
          {show ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}